import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { api, notifyError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Lock, Scales, ArrowRight, CheckCircle, FileArrowDown, X, Warning, Receipt, CalendarX, MagnifyingGlass, ChartLineDown } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PAINS = [
  { icon: Warning, c: "#E63946", t: "Grant clawback risk", d: "Ineligible or poorly evidenced spend gets recovered by the funder months later — after the cash is gone." },
  { icon: Receipt, c: "#FFB020", t: "Scattered receipts", d: "Photos, PDFs and card statements live in five places, with VAT and category rules applied inconsistently." },
  { icon: CalendarX, c: "#5B8DEF", t: "Multi-jurisdiction deadlines", d: "Revenue.ie, HMRC and IRS/Ohio obligations pile up on different calendars with different rules." },
  { icon: MagnifyingGlass, c: "#00A86B", t: "Audit panic", d: "When an auditor or grant body asks for evidence, assembling a defensible trail takes days." },
  { icon: ChartLineDown, c: "#FF7A45", t: "Cash-runway blind spots", d: "Grant drawdowns, invoices and burn are rarely in one live, trustworthy view." },
];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Landing() {
  const [showWaitlist, setShowWaitlist] = useState(false);

  useEffect(() => {
    // Resolve campaign source from the URL (?ref / ?utm_source / ?src) or referrer host.
    const p = new URLSearchParams(window.location.search);
    // Stash a team-invite token so it survives the Google sign-in round-trip.
    const teamInvite = p.get("team_invite");
    if (teamInvite) { try { sessionStorage.setItem("pc_team_invite", teamInvite); } catch { /* no-op */ } }
    const explicit = (p.get("ref") || p.get("utm_source") || p.get("src") || "").trim();
    const campaign = (p.get("utm_campaign") || "").trim();
    let source = explicit;
    if (!source) {
      try { source = document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, "") : "direct"; }
      catch { source = "direct"; }
    }
    // Persist so a later waitlist signup is attributed to the same campaign.
    try { sessionStorage.setItem("pc_source", source); sessionStorage.setItem("pc_campaign", campaign); } catch { /* no-op */ }
    // privacy-safe first-party visit ping (no cookies/PII); fire once per mount
    api.post("/public/track-visit", { path: "/", referrer: document.referrer || "", source, campaign }).catch(() => {});
  }, []);

  const login = async () => {
    // Azure/self-host: use our own Google OAuth when enabled; else Emergent Auth (preview).
    try {
      const cfg = await api.get("/auth/config");
      if (cfg.data.google_enabled) {
        window.location.href = window.location.origin + "/api/auth/google/login";
        return;
      }
    } catch (e) { console.debug("auth config check failed, using Emergent Auth", e); }
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen aurora grain">
      {/* NAV */}
      <header className="hairline-b">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gold-DEFAULT flex items-center justify-center gold-glow" style={{ background: "#FFB020" }}>
              <ShieldCheck size={22} weight="duotone" color="#050B0A" />
            </div>
            <div>
              <div className="font-display text-lg leading-none">PerCen360</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/50 mt-1">Compliance Finance OS</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-white/70">
            <a href="#pains" className="hover:text-white transition">The problem</a>
            <a href="#features" className="hover:text-white transition">Modules</a>
            <Link to="/compatibility" className="hover:text-white transition">Compatibility</Link>
            <a href="#waitlist" className="hover:text-white transition">Waitlist</a>
            <a href="https://percen360.com" className="hover:text-white transition">percen360.com</a>
          </nav>
          <button data-testid="nav-login-btn" onClick={login} className="btn-gold text-sm">
            Sign in <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-24 pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="lg:col-span-7"
        >
          <div className="eyebrow mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-gold-DEFAULT" style={{ background: "#FFB020" }} />
            Invite-only · Launching soon · Ireland · UK · US
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            Stop losing grant money <br />
            <span className="italic font-normal text-white/80">to admin and audit risk.</span> <br />
            <span style={{ color: "#FFB020" }}>One compliance finance OS.</span>
          </h1>
          <p className="mt-8 text-lg text-white/70 max-w-xl leading-relaxed">
            PerCen360 unifies grant tracking, AI receipt capture, multi-jurisdiction deadlines,
            tax reliefs, multi-currency invoicing and cash runway — with an immutable, hash-verified
            audit trail underneath. Built for founders in Ireland, the UK and the US.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <button data-testid="hero-waitlist-btn" onClick={() => setShowWaitlist(true)} className="btn-gold">
              <FileArrowDown size={16} weight="fill" /> Get the white paper &amp; join the waitlist
            </button>
            <button data-testid="hero-login-btn" onClick={login} className="btn-ghost">
              <Lock size={16} weight="fill" /> I have an invite
            </button>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-white/50">
            <span className="flex items-center gap-2"><CheckCircle size={14} weight="fill" color="#00A86B" /> Irish CRO / Revenue rules</span>
            <span className="flex items-center gap-2 text-white/70"><span className="px-1.5 py-0.5 rounded bg-[#FFB020]/15 text-[#FFB020] text-[10px] font-semibold uppercase tracking-wider">New</span> UK Companies House / HMRC — rolling out</span>
            <span className="flex items-center gap-2"><CheckCircle size={14} weight="fill" color="#00A86B" /> Immutable, hash-verified audit trail</span>
            <span className="flex items-center gap-2"><CheckCircle size={14} weight="fill" color="#00A86B" /> Built around GDPR principles</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.9, delay: 0.15 }}
          className="lg:col-span-5"
        >
          <div className="relative">
            <div className="absolute -inset-6 rounded-3xl bg-gradient-to-tr from-[#FFB020]/10 via-transparent to-emerald-pass/10 blur-2xl" />
            <div className="relative glass rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="eyebrow">Grant Guardian</div>
                <span className="badge-warn px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wider uppercase">Live</span>
              </div>
              <div>
                <div className="text-white/60 text-xs">Enterprise Ireland Grant</div>
                <div className="font-display text-4xl mt-1 mono">€20,500 <span className="text-white/40 text-lg">/ €25,000</span></div>
              </div>
              <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: "82%" }} transition={{ duration: 1.2, delay: 0.5 }}
                  className="h-full rounded-full" style={{ background: "linear-gradient(90deg,#00A86B,#FFB020)" }} />
              </div>
              <div className="grid grid-cols-3 gap-3 pt-2">
                {[
                  { l: "Equipment", v: "€8.2k", c: "#00A86B" },
                  { l: "Marketing", v: "€6.1k", c: "#FFB020" },
                  { l: "Training", v: "€6.2k", c: "#E63946" },
                ].map(x => (
                  <div key={x.l} className="rounded-xl bg-black/30 border border-white/5 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-white/50">{x.l}</div>
                    <div className="mono text-sm mt-1" style={{ color: x.c }}>{x.v}</div>
                  </div>
                ))}
              </div>
              <div className="pt-2 flex items-center justify-between text-xs">
                <span className="text-white/50">Audit hash</span>
                <span className="mono text-white/70">7c8a·b19e·f4d2</span>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Pain points */}
      <section id="pains" className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16 border-t border-white/5">
        <div className="eyebrow mb-3" style={{ color: "#FFB020" }}>The problem</div>
        <h2 className="font-display text-3xl sm:text-4xl max-w-2xl leading-tight mb-10">Grant-funded founders carry a disproportionate compliance burden.</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pain-points">
          {PAINS.map((p, i) => (
            <motion.div key={p.t}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="glass rounded-xl p-6 h-full">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: `${p.c}1A`, border: `1px solid ${p.c}44` }}>
                <p.icon size={20} weight="duotone" color={p.c} />
              </div>
              <div className="font-display text-lg">{p.t}</div>
              <div className="text-white/60 text-sm mt-2 leading-relaxed">{p.d}</div>
            </motion.div>
          ))}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="rounded-xl p-6 h-full flex flex-col justify-center" style={{ background: "linear-gradient(135deg,#00A86B22,#FFB02011)", border: "1px solid #FFB02033" }}>
            <div className="font-display text-lg text-[#FFB020]">PerCen360 fixes all of this in one system.</div>
            <button data-testid="pains-waitlist-btn" onClick={() => setShowWaitlist(true)} className="btn-gold mt-4 self-start text-sm">
              Read the white paper <ArrowRight size={15} weight="bold" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Feature strip */}
      <section id="features" className="max-w-[1400px] mx-auto px-6 sm:px-10 py-16 border-t border-white/5">
        <div className="eyebrow mb-6">Modules</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { t: "Receipts + BIK/Alcohol check", d: "Snap or upload — AI extracts vendor, VAT, category, and enforces Irish Revenue rules." },
            { t: "Bank & Wallet Import", d: "Drag any CSV. Auto-categorise transactions with Claude." },
            { t: "Grant Guardian", d: "Enterprise Ireland, Horizon Europe & more — real-time utilisation & export-ready compliance reports." },
            { t: "P&L + Cashflow", d: "Live statements from your receipts and bank feeds." },
            { t: "Accountant Portal", d: "Invite your accountant read-only. GDPR consent recorded." },
            { t: "Compliance Advisor", d: "Multi-turn chat with a Revenue.ie / GDPR expert powered by Claude 4.5." },
          ].map((f,i) => (
            <motion.div key={f.t}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="glass rounded-xl p-6 h-full hover:-translate-y-1 hover:shadow-[0_12px_40px_-8px_rgba(255,176,32,0.2)] transition-all duration-300"
            >
              <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center mb-4">
                <Scales size={20} weight="duotone" color="#FFB020" />
              </div>
              <div className="font-display text-lg">{f.t}</div>
              <div className="text-white/60 text-sm mt-2 leading-relaxed">{f.d}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section id="waitlist" className="max-w-[1400px] mx-auto px-6 sm:px-10 py-24 border-t border-white/5">
        <div className="glass rounded-2xl p-10 sm:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <div className="eyebrow mb-3">Invite-only ahead of launch</div>
            <h2 className="font-display text-3xl sm:text-4xl max-w-xl leading-tight">Join the waitlist and get the white paper.</h2>
            <p className="text-white/60 text-sm mt-3 max-w-lg">Add your details to receive your invite code, early-access pricing and the full product white paper.</p>
          </div>
          <button data-testid="cta-waitlist-btn" onClick={() => setShowWaitlist(true)} className="btn-gold text-base">
            Request early access <ArrowRight size={18} weight="bold" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-10">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="flex items-center gap-3">
            <ShieldCheck size={20} weight="duotone" color="#FFB020" />
            <div className="text-sm text-white/60">© {new Date().getFullYear()} PerCen360 by Gillian Berry</div>
          </div>
          <div className="flex gap-6 text-sm text-white/60">
            <Link to="/why" className="hover:text-white transition">Why PerCen360</Link>
            <Link to="/handout/web" className="hover:text-white transition">Web one-pager</Link>
            <Link to="/handout/mobile" className="hover:text-white transition">Mobile one-pager</Link>
            <Link to="/privacy" className="hover:text-white transition">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition">Terms</Link>
            <Link to="/dpia" className="hover:text-white transition">DPIA</Link>
          </div>
        </div>
      </footer>

      {showWaitlist && <WaitlistModal onClose={() => setShowWaitlist(false)} />}
    </div>
  );
}

function WaitlistModal({ onClose }) {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [wlId, setWlId] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const downloadWhitepaper = (id) => {
    const q = (id || wlId) ? `?wl=${encodeURIComponent(id || wlId)}` : "";
    window.open(`${BACKEND}/api/public/whitepaper.pdf${q}`, "_blank", "noopener");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Please accept the privacy notice to continue"); return; }
    setSubmitting(true);
    try {
      const src = (() => { try { return sessionStorage.getItem("pc_source") || "landing"; } catch { return "landing"; } })();
      const campaign = (() => { try { return sessionStorage.getItem("pc_campaign") || ""; } catch { return ""; } })();
      const r = await api.post("/public/waitlist", { ...form, consent, source: src, campaign });
      setWlId(r.data.waitlist_id);
      setDone(true);
      downloadWhitepaper(r.data.waitlist_id);
      toast.success("You're on the waitlist — your white paper is downloading");
    } catch (err) { notifyError(err); } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
        className="glass rounded-2xl p-7 w-full max-w-md max-h-[92vh] overflow-auto" data-testid="waitlist-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><FileArrowDown size={20} className="text-[#FFB020]" /> <span className="font-display text-xl">Get the white paper</span></div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="waitlist-close"><X size={20} /></button>
        </div>

        {done ? (
          <div className="text-center py-6" data-testid="waitlist-success">
            <CheckCircle size={44} weight="fill" className="mx-auto mb-3 text-[#00A86B]" />
            <div className="font-display text-2xl mb-2">You&apos;re on the list</div>
            <p className="text-white/60 text-sm mb-5">Your white paper should be downloading. We&apos;ll email your invite code as we open access.</p>
            <button onClick={() => downloadWhitepaper()} data-testid="waitlist-redownload" className="btn-ghost text-sm">Download again</button>
          </div>
        ) : (
          <>
            <p className="text-sm text-white/55 mb-4">Join the launch waitlist and download the full product white paper. We&apos;ll send your invite code when access opens.</p>
            <form onSubmit={submit} className="space-y-3">
              <div><label>Full name</label><input required data-testid="wl-name" value={form.name} onChange={set("name")} placeholder="Jane Founder" /></div>
              <div><label>Company</label><input data-testid="wl-company" value={form.company} onChange={set("company")} placeholder="Acme Health" /></div>
              <div><label>Work email</label><input required type="email" data-testid="wl-email" value={form.email} onChange={set("email")} placeholder="jane@company.com" /></div>
              <div><label>Phone (optional)</label><input data-testid="wl-phone" value={form.phone} onChange={set("phone")} placeholder="+353 …" /></div>
              <label className="flex items-start gap-2 text-xs text-white/60 pt-1 cursor-pointer">
                <input type="checkbox" data-testid="wl-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ width: "auto", marginTop: 2 }} />
                <span>I agree that PerCen360 may store these details to contact me about early access. See our <Link to="/privacy" className="text-[#FFB020] hover:underline" target="_blank">Privacy Policy</Link>.</span>
              </label>
              <button type="submit" disabled={submitting} data-testid="wl-submit" className="btn-gold w-full justify-center mt-1">
                {submitting ? "Submitting…" : "Download & join waitlist"} <ArrowRight size={15} weight="bold" />
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
